import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { FileText, Gauge, Plus, AlertTriangle, Trash2, Loader2, RefreshCw } from "lucide-react";
import { ProvinciaField } from "@/components/condomini/BuildingForm";
import { IbanField } from "@/components/IbanField";
import { EMPTY_RESIDENT } from "@/components/condomini/condominiHelpers";

export const ImportPreviewDialog = ({ preview, setPreview, confirming, onConfirm }) => {
  const updPBuilding = (k, v) => setPreview((p) => ({ ...p, building: { ...p.building, [k]: v } }));
  const updPResident = (i, k, v) => setPreview((p) => { const rs = [...p.residents]; rs[i] = { ...rs[i], [k]: v }; return { ...p, residents: rs }; });
  const removePResident = (i) => setPreview((p) => ({ ...p, residents: p.residents.filter((_, idx) => idx !== i) }));
  const addPResident = () => setPreview((p) => ({ ...p, residents: [...p.residents, { ...EMPTY_RESIDENT }] }));

  // Validazione "bassa confidenza": evidenzia le celle dal formato dubbio (da verificare)
  const previewTotMill = (preview?.residents || []).reduce((s, r) => s + (parseFloat(String(r.millesimi ?? "").replace(",", ".")) || 0), 0);
  const previewMillOk = Math.round(previewTotMill) === 1000;
  const cellWarn = (r, field) => {
    const v = (r?.[field] ?? "").toString().trim();
    switch (field) {
      case "nome": return !v;
      case "codice_fiscale": return !!v && !/^[A-Za-z0-9]{16}$/.test(v);
      case "email": return !!v && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v);
      case "telefono": return !!v && v.replace(/[^0-9]/g, "").length < 6;
      case "millesimi":
        if (v && !/^[0-9]+([.,][0-9]+)?$/.test(v)) return true;
        if (!v && ((r?.ruolo || "").toLowerCase() === "proprietario" || !previewMillOk)) return true;
        return false;
      default: return false;
    }
  };
  const warnClass = (r, field) => cellWarn(r, field)
    ? "ring-1 ring-amber-400 bg-amber-50 dark:bg-amber-950/40 dark:ring-amber-500"
    : "";
  const previewWarnCount = (preview?.residents || []).reduce((acc, r) =>
    acc + ["nome", "codice_fiscale", "email", "telefono", "millesimi"].filter((f) => cellWarn(r, f)).length, 0);
  const previewMillMissing = (preview?.residents || []).filter((r) => cellWarn(r, "millesimi")).length;

  return (
    <Dialog open={!!preview} onOpenChange={(o) => { if (!o && !confirming) setPreview(null); }}>
      <DialogContent className="max-w-7xl w-[97vw] max-h-[92vh] overflow-y-auto" data-testid="import-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-[#007BFF]" />Anteprima import — verifica e correggi</DialogTitle>
          <DialogDescription>
            L'AI ha estratto i dati da <strong>{preview?.filename}</strong>. Controlla e correggi prima di salvare.
            {preview?.target_building ? <> I condòmini verranno importati nel condominio <strong>{preview.target_building.name}</strong>.</> : null}
          </DialogDescription>
        </DialogHeader>

        {preview && (
          <div className="space-y-5 min-w-0">
            {preview.building_id && preview.target_building && (
              <div data-testid="import-target-warning" className="flex items-start gap-2.5 rounded-lg border border-amber-300 bg-amber-50 p-3.5 text-[13px] text-amber-900">
                <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5 text-amber-600" />
                <div>
                  Stai importando <b>nel condominio esistente «{preview.target_building.name}»</b>: i condòmini verranno <b>aggiunti a questa anagrafica</b>.
                  <div className="mt-1 text-amber-800">Se invece questo file è di un <b>altro condominio</b>, annulla e usa <b>«Importa nuovo condominio»</b> per crearne uno nuovo, così non mescoli le anagrafiche.</div>
                </div>
              </div>
            )}
            {!preview.building_id && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <div className="sm:col-span-2 lg:col-span-3">
                  <label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Nome condominio</label>
                  <Input value={preview.building.name || ""} onChange={(e) => updPBuilding("name", e.target.value)} data-testid="preview-building-name" className="mt-1" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Indirizzo</label>
                  <Input value={preview.building.address || ""} onChange={(e) => updPBuilding("address", e.target.value)} className="mt-1" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Città</label>
                  <Input value={preview.building.citta || ""} onChange={(e) => updPBuilding("citta", e.target.value)} className="mt-1" />
                </div>
                <div data-testid="preview-building-provincia">
                  <ProvinciaField data={preview.building} onChange={(updater) => setPreview((p) => ({ ...p, building: updater(p.building) }))} />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">CAP</label>
                  <Input value={preview.building.cap || ""} onChange={(e) => updPBuilding("cap", e.target.value)} className="mt-1" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Codice fiscale</label>
                  <Input value={preview.building.codice_fiscale || ""} onChange={(e) => updPBuilding("codice_fiscale", e.target.value)} className="mt-1" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">IBAN</label>
                  <IbanField testId="preview-building-iban" value={preview.building.iban || ""} onChange={(v) => updPBuilding("iban", v)} />
                </div>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
                <span className="text-sm font-semibold">Condòmini estratti ({preview.residents.length})</span>
                <div className="flex items-center gap-3">
                  {(preview.matched_count > 0) && (
                    <span data-testid="preview-matched-count" className="text-[12px] font-semibold inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700"
                      title="Condòmini già presenti in anagrafica: verranno aggiornati con i dati importati">
                      <RefreshCw className="h-3.5 w-3.5" />{preview.matched_count} da aggiornare
                    </span>
                  )}
                  <span data-testid="preview-total-millesimi"
                    className={`text-[12px] font-semibold inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${previewMillOk ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}
                    title={previewMillOk ? "Totale millesimi corretto" : "I millesimi di proprietà dovrebbero sommare 1000"}>
                    <Gauge className="h-3.5 w-3.5" />Tot. millesimi: {previewTotMill.toLocaleString("it-IT", { maximumFractionDigits: 2 })}{previewMillOk ? "" : " / 1000"}
                  </span>
                  <button onClick={addPResident} data-testid="preview-add-row" className="text-[12px] font-medium text-[#007BFF] hover:underline inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" />Aggiungi riga</button>
                </div>
              </div>
              {!previewMillOk && preview.residents.length > 0 && (
                <div className="mb-2 text-[12px] text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-900/50 rounded-md px-3 py-2 flex items-start gap-2" data-testid="preview-millesimi-alert">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
                  <span>
                    Il totale dei <strong>millesimi di proprietà</strong> è <strong>{previewTotMill.toLocaleString("it-IT", { maximumFractionDigits: 2 })}</strong> invece di <strong>1000</strong>.
                    {previewMillMissing > 0
                      ? <> Controlla e correggi {previewMillMissing === 1 ? "la riga evidenziata" : `le ${previewMillMissing} righe evidenziate`} in giallo (millesimi mancanti o non validi) prima di confermare.</>
                      : <> Verifica i valori nelle righe qui sotto: la somma dei millesimi di proprietà dovrebbe essere 1000.</>}
                  </span>
                </div>
              )}
              {previewWarnCount > 0 && (
                <div className="mb-2 text-[12px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 rounded-md px-3 py-1.5 flex items-center gap-2" data-testid="preview-warn-legend">
                  <span className="inline-block h-3 w-3 rounded-sm ring-1 ring-amber-400 bg-amber-100 dark:bg-amber-900/60" />
                  {previewWarnCount} {previewWarnCount === 1 ? "campo da verificare" : "campi da verificare"}: le celle evidenziate in giallo hanno un formato dubbio (es. C.F. non di 16 caratteri, email/telefono non validi, millesimi mancanti o non numerici).
                </div>
              )}
              <div className="border border-border rounded-lg overflow-x-auto min-w-0">
                <table className="w-full text-[13px]" data-testid="preview-residents-table">
                  <thead className="bg-secondary/60 text-muted-foreground">
                    <tr>
                      {["Nominativo", "Stato", "Scala", "Interno", "Millesimi", "C.F.", "Email", "Telefono", "Ruolo", ""].map((h) => (
                        <th key={h} className="text-left font-semibold px-2 py-2 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/60">
                    {preview.residents.map((r, i) => (
                      <tr key={i} data-testid={`preview-row-${i}`} className={cellWarn(r, "millesimi") ? "bg-amber-50/70 dark:bg-amber-950/20" : ""}>
                        <td className="px-1.5 py-1">
                          <Input value={r.nome || ""} onChange={(e) => updPResident(i, "nome", e.target.value)} className={`h-8 min-w-[150px] ${warnClass(r, "nome")}`} />
                        </td>
                        <td className="px-1.5 py-1 whitespace-nowrap">
                          {r.matched ? (
                            <span data-testid={`preview-status-${i}`} title="Condòmino già presente in anagrafica: i dati verranno aggiornati con quelli importati"
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
                              <RefreshCw className="h-3 w-3" />Verrà aggiornato
                              <span data-testid={`preview-matched-${i}`} className="hidden" />
                            </span>
                          ) : (
                            <span data-testid={`preview-status-${i}`} title="Nuovo condòmino: verrà aggiunto all'anagrafica"
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-sky-100 text-sky-700 border border-sky-200">
                              <Plus className="h-3 w-3" />Nuovo
                            </span>
                          )}
                        </td>
                        <td className="px-1.5 py-1"><Input value={r.scala || ""} onChange={(e) => updPResident(i, "scala", e.target.value)} className="h-8 w-14" /></td>
                        <td className="px-1.5 py-1"><Input value={r.interno || ""} onChange={(e) => updPResident(i, "interno", e.target.value)} className="h-8 w-16" /></td>
                        <td className="px-1.5 py-1"><Input value={r.millesimi || ""} onChange={(e) => updPResident(i, "millesimi", e.target.value)} className={`h-8 w-20 ${warnClass(r, "millesimi")}`} /></td>
                        <td className="px-1.5 py-1"><Input value={r.codice_fiscale || ""} onChange={(e) => updPResident(i, "codice_fiscale", e.target.value)} className={`h-8 min-w-[150px] ${warnClass(r, "codice_fiscale")}`} /></td>
                        <td className="px-1.5 py-1"><Input value={r.email || ""} onChange={(e) => updPResident(i, "email", e.target.value)} className={`h-8 min-w-[160px] ${warnClass(r, "email")}`} /></td>
                        <td className="px-1.5 py-1"><Input value={r.telefono || ""} onChange={(e) => updPResident(i, "telefono", e.target.value)} className={`h-8 min-w-[120px] ${warnClass(r, "telefono")}`} /></td>
                        <td className="px-1.5 py-1">
                          <select value={(r.ruolo || "").toLowerCase()} onChange={(e) => updPResident(i, "ruolo", e.target.value)} className="h-8 rounded-md border border-border bg-background px-2 text-[13px]">
                            <option value="">—</option>
                            <option value="proprietario">Proprietario</option>
                            <option value="inquilino">Inquilino</option>
                          </select>
                        </td>
                        <td className="px-1.5 py-1">
                          <button onClick={() => removePResident(i)} data-testid={`preview-remove-${i}`} className="text-red-500 hover:text-red-600 p-1" title="Rimuovi"><Trash2 className="h-4 w-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => setPreview(null)} disabled={confirming} data-testid="preview-cancel">Annulla</Button>
          <Button onClick={onConfirm} disabled={confirming || !preview?.residents?.length} data-testid="preview-confirm" className="bg-[#007BFF] hover:bg-[#0069D9]">
            {confirming ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Importazione…</> : `Conferma e importa (${preview?.residents?.length || 0})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
